import React, { useMemo } from 'react'
import { useQuery } from 'jsonapi-react'
import { Helmet } from 'react-helmet-async'
import { Layout, Sidebar, TwoColumns } from 'app/theme/layout'
import Breadcrumbs from 'app/theme/Breadcrumbs'
import CityDropdown from 'app/theme/CityDropdown'
import MapListToggle from 'app/theme/MapListToggle'
import PillToggle from 'app/theme/PillToggle'
import EventMap from 'app/theme/EventMap'
import BookTruckBanner from 'app/theme/banners/BookTruckBar'
import ShadowBox from 'app/caterers/ShadowBox'
import Pagination from 'app/theme/Pagination'
import NotFoundPage from 'app/pages/NotFoundPage'
import { format } from 'date-fns'

const monthYear = format(new Date(), 'MMMM yyyy')

export default function RegionPage({ region, page, tagSlug }) {
  const path = `/food-trucks/${region.url_slug}`

  const query = useQuery(['caterers', {
    sort: '-ranking',
    filter: {
      region_id: region.id,
      tag_slug: tagSlug || undefined,
      closed: false,
    },
    include: [
      'photo',
    ],
    fields: {
      caterers: ['id', 'name', 'slug'],
      photo: ['path']
    },
    page: {
      number: page || 1,
      size: 21
    },
    stats: {
      total: 'count',
    }
  }])

  const { data: [tag] = [] } = useQuery(tagSlug && ['tags', {
    filter: {
      slug: tagSlug,
    },
    fields: {
      tags: ['name', 'slug']
    }
  }])

  const crumbs = [
    { text: 'Food Trucks', url: `/food-trucks/` },
  ]

  if (tag) {
    crumbs.push({ text: region.name, url: `${path}/1/` })
    crumbs.push({ text: tag.name })
  } else {
    crumbs.push({ text: region.name })
  }

  const isMapPage = !page && !tagSlug

  let caterers = query.data ? [...query.data] : []

  const googleMetaTags = useMemo(() => {
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      'itemListElement': caterers.map((caterer, index) => ({
        '@type': 'ListItem',
        position: index,
        url: `https://roaminghunger.com/${caterer.slug}/`
      }))
    })
  }, [caterers])

  if (caterers.length && page || tagSlug) {
    caterers.splice(3, 0, 'banner')
  }

  const pageOutOfRange = page && Math.ceil(query.meta?.stats?.total?.count / 21) < page

  if (pageOutOfRange) {
    return <NotFoundPage />
  }

  return (
    <Layout className="container py-12">
      <TwoColumns sidebar={<Sidebar region={region} />}>
        <Breadcrumbs
          crumbs={crumbs}
        />
        <div className="mt-4 flex items-center">
          <h1 className="text-3xl lg:text-4xl text-navy font-platform leading-tight">
            {region.name} Food Trucks
          </h1>
          <CityDropdown
            cities={region.cities}
            className="ml-3 mt-2"
          />
        </div>
        <div>
          {region.short_description}
        </div>
        {isMapPage ? (
          <div>
            <Helmet>
              <title>Food Truck Map - {region.name} - Catering & Events - Roaming Hunger</title>
              <meta name="description" content={`Locate the best Food Trucks near you in ${region.name} and find the perfect food truck to cater your office, party, wedding or next event.`}></meta>
              <meta name="og:title" content={`Food Truck Map - ${region.name} - Catering & Events`}></meta>
              <script type="application/ld+json">
                {googleMetaTags}
              </script>
            </Helmet>
            <div className="sm:flex justify-between items-center pt-4 mt-4 mb-1 border-t border-gray-300">
              <div className="text-navy text-2xl font-platform">
                Live Map
              </div>
              <MapListToggle
                active="map"
                mapUrl={`${path}/`}
                listUrl={`${path}/1/`}
              />
            </div>
            <EventMap
              lat={region.location.lat}
              lng={region.location.lng}
              center={`${region.name}, ${region.country.name}`}
              boundingBox={region.bounding_box}
            />
            <BookTruckBanner
              className="mt-6"
            />
            <div className="mt-8 mb-5 text-navy text-4xl font-platform">
              Food Trucks
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {caterers.map(caterer => (
                <ShadowBox
                  key={caterer.id}
                  caterer={caterer}
                  photo={caterer.photo}
                />
              ))}
            </div>
          </div>
        ) : (
          <div>
            <Helmet>
              <title>The 21 Best {region.name} Food Trucks ({monthYear}) - Roaming Hunger</title>
              <meta name="description" content={`The 21 Best Food Trucks in ${region.name} for corporate catering, events, parties, and street service.`}></meta>
              <meta name="og:title" content={`The 21 Best ${region.name} Food Trucks (${monthYear})`}></meta>
              <script type="application/ld+json">
                {googleMetaTags}
              </script>
            </Helmet>
            <div
              className={[
                'justify-between items-center mt-4 pt-4 pb-3',
                'border-t border-gray-300',
                'sm:flex sm:pb-1 md:mb-2'
              ]}
            >
              <MapListToggle
                active="list"
                mapUrl={`${path}/`}
                listUrl={`${path}/1/`}
                className="sm:order-2"
              />
              <PillToggle
                pills={[
                  { text: 'Preferred', url: `${path}/1/`, active: !tagSlug },
                  { text: 'Savory', url: `${path}/savory/`, active: tagSlug === 'savory' },
                  { text: 'Sweet', url: `${path}/sweet/`, active: tagSlug === 'sweet' },
                  { text: 'Vegetarian', url: `${path}/vegetarian/`, active: tagSlug === 'vegetarian' },
                ]}
              />
            </div>
            {tag && (
              <div className="text-navy leading-none text-xl mb-6 md:text-2xl">
                {tag.name} Food Trucks and {tag.name} Food Carts in {region.name}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 lg:gap-6">
              {caterers.map((caterer, index) => (
                caterer === 'banner' ? (
                  <div key="banner" className="hidden md:flex md:col-span-3">
                    <BookTruckBanner />
                  </div>
                ) : (
                  <ShadowBox
                    key={caterer.id}
                    caterer={caterer}
                    photo={caterer.photo}
                  />
                )
              ))}
            </div>
            {query.meta && (
              <Pagination
                className="justify-center mt-10"
                total={query.meta.stats.total.count}
                current={page || 1}
                limit={21}
                getLinkProps={link => ({
                  href: tag
                    ? `${path}/${tag.slug}/${link.page === 1 ? '' : `${link.page}/`}`
                    : `${path}/${link.page}/`
                })}
              />
            )}
          </div>
        )}
      </TwoColumns>
    </Layout>
  )
}
